import { useRef, useCallback } from 'react';

/**
 * useAudioMixer
 *
 * Gère la capture et le mixage de deux sources audio :
 *  - Le microphone de l'utilisateur (getUserMedia)
 *  - Le son système / onglet (getDisplayMedia)
 *
 * Retourne :
 *  - initAudio()         → démarre la capture, retourne { mixedStream, analyser, videoTrack }
 *  - setMicGain(0-2)     → ajuste le volume du micro en temps réel
 *  - setSysGain(0-2)     → ajuste le volume du son système en temps réel
 *  - getMicLabel()       → nom du périphérique micro
 *  - getSysLabel()       → nom de la source système
 *  - cleanup()           → arrête tous les tracks et ferme l'AudioContext
 */
export function useAudioMixer() {
  const audioCtxRef   = useRef(null);
  const micStreamRef  = useRef(null);
  const sysStreamRef  = useRef(null);
  const micGainRef    = useRef(null);
  const sysGainRef    = useRef(null);

  // -------------------------------------------------------------
  // initAudio
  // Appelé au démarrage de l'enregistrement.
  // Retourne le stream final mixé + l'analyser pour la waveform.
  // -------------------------------------------------------------
  const initAudio = useCallback(async () => {

    // 1. Demande accès micro avec traitement audio activé
    const micStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,   // supprime l'écho de la salle
        noiseSuppression: true,   // réduit le bruit de fond
        sampleRate: 48000,        // qualité CD
      },
      video: false,
    });
    micStreamRef.current = micStream;

    // 2. Demande le partage d'écran + son système
    // preferCurrentTab et displaySurface: 'browser' pré-sélectionnent
    // l'onglet actuel dans la boîte de dialogue (Chromium / Brave)
    const sysStream = await navigator.mediaDevices.getDisplayMedia({
      video: {
        displaySurface: 'browser',         // pré-sélectionne l'onglet dans la liste
      },
      audio: {
        echoCancellation: false,
        noiseSuppression: false,
        sampleRate: 48000,
        suppressLocalAudioPlayback: false, // évite de couper le son local
      },
      preferCurrentTab: true,              // pré-sélectionne l'onglet en cours
      selfBrowserSurface: 'include',       // autorise la capture de l'onglet lui-même
    });
    sysStreamRef.current = sysStream;

    // 3. Création de l'AudioContext — moteur de traitement audio
    const audioCtx = new AudioContext({ sampleRate: 48000 });
    audioCtxRef.current = audioCtx;

    // 4. Destination : là où les pistes mixées seront envoyées
    const destination = audioCtx.createMediaStreamDestination();

    // 5. Branchement du micro → GainNode → destination
    const micSource = audioCtx.createMediaStreamSource(micStream);
    const micGain   = audioCtx.createGain();
    micGain.gain.value = 1; // 100% par défaut
    micSource.connect(micGain);
    micGain.connect(destination);
    micGainRef.current = micGain;

    // 6. Branchement du son système → GainNode → destination
    const sysAudioTracks = sysStream.getAudioTracks();
    if (sysAudioTracks.length > 0) {
      const sysAudioStream = new MediaStream(sysAudioTracks);
      const sysSource      = audioCtx.createMediaStreamSource(sysAudioStream);
      const sysGain        = audioCtx.createGain();
      sysGain.gain.value   = 1;
      sysSource.connect(sysGain);
      sysGain.connect(destination);
      sysGainRef.current = sysGain;
    }

    // 7. Analyser pour la visualisation de la waveform
    // Branché sur la destination mixée (micro + système)
    const analyser   = audioCtx.createAnalyser();
    analyser.fftSize = 512;
    audioCtx.createMediaStreamSource(destination.stream).connect(analyser);

    // 8. Stream final : vidéo de l'écran + audio mixé
    const videoTracks = sysStream.getVideoTracks();
    const mixedStream = new MediaStream([
      ...videoTracks,
      ...destination.stream.getAudioTracks(),
    ]);

    return {
      mixedStream,  // passé à useMediaRecorder
      analyser,     // passé à useWaveform
      videoTrack: videoTracks[0] ?? null,
      hasSysAudio: sysAudioTracks.length > 0,
      micGainNode: micGain,
    };
  }, []);

  // -------------------------------------------------------------
  // Contrôles de gain (0 = muet, 1 = 100%, 2 = 200%)
  // Peuvent être appelés en temps réel pendant l'enregistrement
  // -------------------------------------------------------------
  const setMicGain = useCallback((value) => {
    if (micGainRef.current) {
      micGainRef.current.gain.value = value;
    }
  }, []);

  const setSysGain = useCallback((value) => {
    if (sysGainRef.current) {
      sysGainRef.current.gain.value = value;
    }
  }, []);

  // -------------------------------------------------------------
  // Labels des périphériques pour l'affichage dans l'UI
  // -------------------------------------------------------------
  const getMicLabel = useCallback(() => {
    const tracks = micStreamRef.current?.getAudioTracks() ?? [];
    return tracks[0]?.label || 'Microphone';
  }, []);

  const getSysLabel = useCallback(() => {
    const tracks = sysStreamRef.current?.getAudioTracks() ?? [];
    return tracks[0]?.label || 'Son système';
  }, []);

  // -------------------------------------------------------------
  // Cleanup : arrête tous les tracks et libère l'AudioContext
  // Appelé quand l'enregistrement s'arrête ou le composant démonte
  // -------------------------------------------------------------
  const cleanup = useCallback(() => {
    micStreamRef.current?.getTracks().forEach((t) => t.stop());
    sysStreamRef.current?.getTracks().forEach((t) => t.stop());
    if (audioCtxRef.current?.state !== 'closed') {
      audioCtxRef.current?.close();
    }
    micStreamRef.current = null;
    sysStreamRef.current = null;
    audioCtxRef.current  = null;
    micGainRef.current   = null;
    sysGainRef.current   = null;
  }, []);

  return {
    initAudio,
    setMicGain,
    setSysGain,
    getMicLabel,
    getSysLabel,
    cleanup,
  };
}